import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Sun, Calculator, Lightning, BatteryCharging, CurrencyInr, ChartLineUp, Leaf } from "@phosphor-icons/react";
import { COMPANY, IMAGES, FAQS_SOLAR } from "@/data/site";
import LeadForm from "@/components/LeadForm";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = [
  { size: "3 kWp", houses: "3 BHK Home", units: "~360 units/mo", price: "₹2,10,000", net: "₹1,32,000" },
  { size: "4 kWp", houses: "Large Home", units: "~480 units/mo", price: "₹2,70,000", net: "₹1,92,000" },
  { size: "5 kWp", houses: "Villa / SME", units: "~600 units/mo", price: "₹3,30,000", net: "₹2,52,000" },
];

const TYPES = [
  { t: "On-Grid", d: "Connected to EB grid. Net-metering. PM Surya Ghar subsidy eligible.", best: "Best for homes with daytime usage", color: "bg-amber-400" },
  { t: "Off-Grid", d: "Battery backup. Power even in outages.", best: "Best for areas with EB cuts", color: "bg-emerald-500" },
  { t: "Hybrid", d: "Grid + battery. Best of both worlds.", best: "Best for premium homes & factories", color: "bg-slate-900" },
];

function Calc() {
  const [bill, setBill] = useState(3000);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/solar/calculate`, { monthly_bill: Number(bill) });
      setResult(r.data);
    } catch {
      toast.error("Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-flat p-8 lg:p-10" data-testid="solar-calculator">
      <div className="label-eyebrow">Interactive Tool</div>
      <h3 className="mt-3 font-heading text-3xl font-bold text-slate-950">Solar Savings Calculator</h3>
      <p className="mt-2 text-sm text-slate-500">Based on Tamil Nadu tariff &amp; PM Surya Ghar 2026 subsidy.</p>

      <div className="mt-7">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Your monthly electricity bill (₹)</label>
        <div className="mt-3 flex items-center gap-3">
          <CurrencyInr size={20} weight="bold" className="text-slate-400" />
          <input
            type="range" min="500" max="20000" step="100" value={bill}
            onChange={(e) => setBill(e.target.value)}
            className="flex-1 accent-slate-950"
            data-testid="calc-bill-slider"
          />
          <input
            type="number" value={bill} onChange={(e) => setBill(e.target.value)}
            className="w-28 border border-slate-300 px-3 py-2 text-sm"
            data-testid="calc-bill-input"
          />
        </div>
        <div className="mt-1 text-xs text-slate-500">₹{Number(bill).toLocaleString("en-IN")} / month</div>
      </div>

      <button onClick={calc} disabled={loading} data-testid="calc-submit-btn" className="mt-6 inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-3 text-sm font-semibold hover:bg-slate-700 disabled:opacity-60">
        {loading ? "Calculating..." : "Calculate Savings"} <ArrowRight size={14} weight="bold" />
      </button>

      {result && (
        <div className="mt-8 grid grid-cols-2 gap-px bg-slate-200 border border-slate-200" data-testid="calc-result">
          <div className="bg-white p-5"><div className="label-eyebrow">Recommended</div><div className="mt-2 font-heading text-3xl font-black text-slate-950">{result.recommended_kw} kW</div></div>
          <div className="bg-white p-5"><div className="label-eyebrow">System Cost</div><div className="mt-2 font-heading text-3xl font-black text-slate-950">₹{(result.estimated_cost_inr / 100000).toFixed(2)}L</div></div>
          <div className="bg-white p-5"><div className="label-eyebrow">Govt Subsidy</div><div className="mt-2 font-heading text-3xl font-black text-emerald-600">₹{result.subsidy_estimate_inr.toLocaleString("en-IN")}</div></div>
          <div className="bg-white p-5"><div className="label-eyebrow">Yearly Savings</div><div className="mt-2 font-heading text-3xl font-black text-amber-500">₹{(result.yearly_savings_inr / 1000).toFixed(0)}k</div></div>
          <div className="bg-white p-5"><div className="label-eyebrow">Payback</div><div className="mt-2 font-heading text-3xl font-black text-slate-950">{result.payback_years} yrs</div></div>
          <div className="bg-white p-5"><div className="label-eyebrow">CO₂ Offset</div><div className="mt-2 font-heading text-3xl font-black text-emerald-600">{result.co2_offset_tons_per_year}t / yr</div></div>
        </div>
      )}
    </div>
  );
}

export default function Solar() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div data-testid="solar-page">
      <section className="relative bg-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.solar} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <div className="label-eyebrow">Solar Energy Division</div>
          <h1 className="mt-4 font-heading font-black text-5xl md:text-7xl tracking-tighter text-slate-950 max-w-3xl">
            Smart solar for <span className="italic font-light">homes &amp; businesses.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-slate-700">PM Surya Ghar subsidy. 25-year warranty. Payback in 3-4 years. Across all of Tamil Nadu.</p>
        </div>
      </section>

      <section className="bg-slate-50 py-20" data-testid="solar-sizes-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <div className="label-eyebrow">Residential Solar — Price List</div>
              <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Transparent turnkey pricing.</h2>
            </div>
            <div className="bg-emerald-500 text-white px-6 py-4 max-w-fit" data-testid="subsidy-banner">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-50 font-bold">Govt Subsidy</div>
              <div className="font-heading text-3xl font-black">₹78,000</div>
              <div className="text-xs text-emerald-50">PM Surya Ghar — fixed cap</div>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SIZES.map((s, i) => (
              <div key={i} className={`card-flat p-8 ${i === 1 ? "ring-2 ring-amber-400" : ""}`} data-testid={`solar-size-${i}`}>
                {i === 1 && <div className="inline-block bg-amber-400 text-slate-950 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 mb-4">Most Popular</div>}
                <Sun size={30} weight="duotone" className="text-amber-500" />
                <div className="mt-4 font-heading font-black text-4xl text-slate-950 tracking-tighter">{s.size}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-slate-500 font-semibold">{s.houses} • {s.units}</div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-heading font-black text-3xl text-slate-950">{s.price}</span>
                  <span className="text-xs text-slate-400 line-through">before subsidy</span>
                </div>
                <div className="mt-3 pt-4 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">After ₹78k subsidy</span>
                  <span className="font-heading font-bold text-2xl text-emerald-600">{s.net}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500">*Prices are indicative turnkey costs incl. installation &amp; net-metering. Final quote after free site survey. Subsidy applied post-commissioning via PM Surya Ghar portal.</p>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="solar-types-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">Solar Types</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">On-Grid, Off-Grid &amp; Hybrid.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TYPES.map((t, i) => (
              <div key={i} className="card-flat p-8" data-testid={`solar-type-${i}`}>
                <div className={`inline-block ${t.color} w-12 h-1 mb-5`} />
                <h3 className="font-heading text-2xl font-bold text-slate-950">{t.t}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{t.d}</p>
                <div className="mt-5 pt-5 border-t border-slate-200 text-xs uppercase tracking-wider font-semibold text-slate-700">{t.best}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" data-testid="solar-commercial-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow">Commercial &amp; Industrial</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Cut your factory bill by 70%.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">From 25 kW to 1 MW. Custom solutions for factories, schools, hospitals and hotels. Accelerated depreciation, capex &amp; opex models available.</p>
            <ul className="mt-8 space-y-3 text-sm">
              {["Factories &amp; Industrial Plants", "Schools &amp; Universities", "Hospitals &amp; Clinics", "Hotels &amp; Resorts"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-slate-800"><Lightning size={16} weight="fill" className="text-amber-500" /><span dangerouslySetInnerHTML={{ __html: x }} /></li>
              ))}
            </ul>
          </div>
          <div className="card-flat overflow-hidden">
            <img src={IMAGES.solar} alt="Commercial solar" className="w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="calc-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="label-eyebrow">Calculate</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">See your savings in 10 seconds.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Drag the slider to your average monthly EB bill. We'll show the right system size, subsidy &amp; payback.</p>
            <div className="mt-8 flex flex-col gap-3">
              <div className="flex items-center gap-3 text-sm text-slate-700"><Calculator size={20} weight="duotone" className="text-amber-500" /> Live TN tariff (₹7/unit)</div>
              <div className="flex items-center gap-3 text-sm text-slate-700"><ChartLineUp size={20} weight="duotone" className="text-emerald-500" /> PM Surya Ghar 2026 subsidy</div>
              <div className="flex items-center gap-3 text-sm text-slate-700"><Leaf size={20} weight="duotone" className="text-emerald-500" /> CO₂ offset projection</div>
              <div className="flex items-center gap-3 text-sm text-slate-700"><BatteryCharging size={20} weight="duotone" className="text-blue-500" /> 25-year generation forecast</div>
            </div>
          </div>
          <div className="md:col-span-7"><Calc /></div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" data-testid="solar-faq-section">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">FAQ</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Quick answers.</h2>
          <div className="mt-10 border-t border-slate-200">
            {FAQS_SOLAR.map((f, i) => (
              <div key={i} className="border-b border-slate-200" data-testid={`solar-faq-${i}`}>
                <button onClick={() => setOpenIdx(openIdx === i ? -1 : i)} className="w-full py-5 flex items-center justify-between text-left">
                  <span className="font-heading font-semibold text-slate-950 text-base lg:text-lg pr-6">{f.q}</span>
                  <span className="text-2xl text-slate-400 shrink-0">{openIdx === i ? "−" : "+"}</span>
                </button>
                {openIdx === i && <p className="pb-5 text-slate-600 leading-relaxed text-sm" data-testid={`solar-faq-answer-${i}`}>{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8"><LeadForm source="solar" /></div>
      </section>
    </div>
  );
}
